<template>
  <div class="event-sessions-modal">
    <CustomModal :show="show"
                 @close="onClose">
      <template #header>
        <h1 class="custom-modal__heading">
          Event sessions
        </h1>
      </template>

      <div class="event-sessions-modal__body">
        <p>This event is comprised of {{ numSessions }} {{ pluralized }}.</p>

        <template v-if="hasSessions">
          <div
            v-for="(session, index) in sessions"
            :key="index"
            class="event-sessions-modal__session">
            <h3>
              {{ session.name || `Session ${index + 1}` }}
            </h3>

            <div v-if="session.description">
              <span class="event-sessions-modal__item-heading">
                Description:
              </span>
              <!-- eslint-disable-next-line vue/no-v-html -->
              <span v-html="session.description">
              </span>
            </div>

            <div v-if="session.startDateTime">
              <span class="event-sessions-modal__item-heading">
                Start time:
              </span>
              {{ session.startDateTime }}
            </div>

            <div v-if="session.endDateTime">
              <span class="event-sessions-modal__item-heading">
                End time:
              </span>
              {{ session.endDateTime }}
            </div>

            <div class="event-sessions-modal__address">
              {{ session.address.line1 }}
              <br v-if="session.address.line1">
              {{ session.address.line2 }}
              <br v-if="session.address.line2">
              {{ session.address.town }}
              <br v-if="session.address.town">
              {{ session.address.region }}
              <br v-if="session.address.region">
              {{ session.address.postcode.toUpperCase() }}
            </div>
          </div>
        </template>

        <template v-else>
          Could not find session data.
        </template>
      </div>

      <template #footer>
        <button class="btn btn-accent-6 btn-rounded"
                aria-label="Close modal"
                @click="onClose">
          Close
        </button>
      </template>
    </CustomModal>
  </div>
</template>

<script>
import CustomModal from "~/Components/CustomModal.vue";

// #region types
/**
 * @typedef {Object} Address
 * @property {string} addressId UUID
 * @property {string} orgId UUID
 * @property {string} orgName
 * @property {string} line1
 * @property {string} line2
 * @property {string} line3
 * @property {string} town
 * @property {string} region
 * @property {string} postcode
 * @property {number} [countryId]
 * @property {string} country
 */

/**
 * @typedef {Object} Session
 * @property {string} name
 * @property {string} description
 * @property {string} endDateTime Date is already a formatted string
 * @property {string} startDateTime Date is already a formatted string
 * @property {Address} address
 * @property {boolean} limitNumberOfSpaces
 * @property {boolean} [mandatory]
 * @property {number} [maxSpaces]
 * @property {number} [spacesAvailable]
 * @property {number} [spacesBooked]
 */

/**
 * @typedef {Object} EventInstance
 * @property {string} id UUID
 * @property {string} eventId UUID
 * @property {string} [defaultSessionId] UUID
 * @property {number} [productId]
 * @property {boolean} showLocation
 * @property {boolean} showDisabilityOptions
 * @property {boolean} showDietaryRequirements
 * @property {string} location
 * @property {string} duration
 * @property {string} startDate Date is already a formatted string
 * @property {string} endDate Date is already a formatted string
 * @property {string} address
 * @property {string} postcode
 * @property {string} priceExVat
 * @property {number} [vatRate] double
 * @property {boolean} applyVat
 * @property {number} productId
 * @property {number} [spacesAvailable]
 * @property {number} [minDelegates]
 * @property {number} [maxDelegates]
 * @property {number} [maxDelegatesPerOrganisation]
 * @property {number} [maxDelegatesOnWaitingList]
 * @property {Array<Session>} sessions
 * @property {string} instanceType
 */
// #endregion types

export default {
  name: "EventSessionsModal",
  components: {
    CustomModal
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    /** @type {import('vue').PropType<EventInstance>} */
    instance: {
      type: Object,
      default: () => ( {} )
    },
    eventTitle: {
      type: String,
      default: null
    }
  },
  computed: {
    sessions () {
      let sessions = [];
      if ( this.instance && this.instance.sessions ) {
        sessions = this.instance.sessions;
      }
      return sessions;
    },
    hasSessions () {
      return this.sessions && this.sessions.length > 0;
    },
    numSessions () {
      return this.sessions.length;
    },
    pluralized () {
      return `session${this.numSessions > 1 ? "s" : ""}`;
    }
  },
  methods: {
    onClose () {
      this.$emit( "close" );
    }
  }
};
</script>

<style lang="less" scoped>
.event-sessions-modal {
  &__subheading {
    font-size: 16px;
    color: #000;
    padding: 15px 0;
  }

  &__item-heading {
    font-weight: 600;
  }

  &__num-delegates {
    max-width: 200px;
  }

  &__delegates-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  &__address {
    margin-top: 10px;
  }

  &__session {
    margin-bottom: 30px;
  }
}
</style>
